import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@/utils";

/**
 * Input
 *
 * A single-line text field with optional label, leading adornment and hint /
 * error text. Errors are announced via `aria-invalid` and the hint region.
 *
 * @example
 *   <Input label="Name" placeholder="Untitled" />
 *   <Input error="Required" defaultValue="" />
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  adornment?: ReactNode;
}

const fieldBase =
  "w-full bg-bg-elevated text-text-primary placeholder:text-text-tertiary " +
  "border border-border rounded-md transition-colors duration-fast ease-standard " +
  "hover:border-border-strong focus:border-border-strong focus-visible:outline " +
  "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus " +
  "disabled:opacity-40 disabled:pointer-events-none selectable";

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, adornment, className, id, ...rest }, ref) => {
    const inputId = id ?? rest.name;
    const describedBy = error || hint ? `${inputId}-desc` : undefined;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-text-secondary"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {adornment && (
            <span className="pointer-events-none absolute left-2.5 text-text-tertiary">
              {adornment}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              fieldBase,
              "h-8 text-base",
              adornment ? "pl-8 pr-3" : "px-3",
              error && "border-border-strong",
              className,
            )}
            {...rest}
          />
        </div>
        {(error || hint) && (
          <p
            id={describedBy}
            className={cn(
              "text-sm",
              error ? "text-text-primary" : "text-text-tertiary",
            )}
          >
            {error ?? hint}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

/** Multi-line variant sharing the same visual language. */
export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, className, id, ...rest }, ref) => {
    const areaId = id ?? rest.name;
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={areaId}
            className="text-sm font-medium text-text-secondary"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={areaId}
          className={cn(fieldBase, "resize-none px-3 py-2 text-base", className)}
          {...rest}
        />
      </div>
    );
  },
);

Textarea.displayName = "Textarea";
